import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  InputAdornment,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Stack,
  Avatar,
  FormControlLabel,
  Divider,
  FormControl,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import { studentService } from 'src/services/student.service';
import { CohortWithDetails } from 'src/types/cohort.types';

interface AssignStudentsModalProps {
  open: boolean;
  onClose: () => void;
  cohort: CohortWithDetails | null;
  allCohorts?: CohortWithDetails[];
  onConfirm: (cohortId: string, studentIds: string[], syncProfileGrade?: boolean) => Promise<void>;
  isSubmitting?: boolean;
}

export function AssignStudentsModal({
  open,
  onClose,
  cohort,
  allCohorts = [],
  onConfirm,
  isSubmitting = false,
}: AssignStudentsModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGradeFilter, setSelectedGradeFilter] = useState<string>('ALL');
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [syncProfileGrade, setSyncProfileGrade] = useState<boolean>(true);

  // Fetch active students for assignment
  const { data: studentResponse, isLoading } = useQuery({
    queryKey: ['students-all-for-cohort-modal'],
    queryFn: async () => {
      const res = await studentService.getStudents({ page: 1, limit: 1000 });
      return res?.data || [];
    },
    enabled: open,
  });

  const students = studentResponse || [];
  const targetGradeName = cohort?.className || cohort?.name || 'Class Grade';

  // Map studentId -> current enrolled cohort name across all cohorts
  const studentOtherCohortMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!allCohorts || !cohort) return map;

    allCohorts.forEach((c) => {
      if (c.id !== cohort.id && c.students) {
        c.students.forEach((s) => {
          map.set(s.studentId, c.name);
        });
      }
    });
    return map;
  }, [allCohorts, cohort]);

  // Sync currently enrolled students when modal opens
  useEffect(() => {
    if (cohort && cohort.students) {
      const currentIds = cohort.students.map((s) => s.studentId);
      setSelectedStudentIds(currentIds);
    } else {
      setSelectedStudentIds([]);
    }
    setSearchTerm('');
    setSelectedGradeFilter('ALL');
    setSyncProfileGrade(true);
  }, [cohort, open]);

  const handleToggleStudent = (id: string) => {
    setSelectedStudentIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Helper to extract clean student view properties
  const getStudentProps = (s: any) => {
    const studentId = s.id || s.studentId;
    const firstname = s.user?.firstname || s.users?.firstname || (s.name ? s.name.split(' ')[0] : '') || s.firstname || '';
    const lastname = s.user?.lastname || s.users?.lastname || (s.name ? s.name.split(' ').slice(1).join(' ') : '') || s.lastname || '';
    const fullName = `${firstname} ${lastname}`.trim() || 'Scholar';
    const email = s.user?.email || s.users?.email || s.email || 'No email';
    const rawGrade = s.grade || s.user?.extendedProperties?.grade || s.user?.extendedproperties?.grade || '';
    const grade = rawGrade ? String(rawGrade).trim() : 'Unassigned';
    const balance = s.balance ?? s.currentPoints ?? 0;
    const otherCohortName = studentOtherCohortMap.get(studentId);

    return { studentId, fullName, email, grade, balance, otherCohortName };
  };

  // Unassigned scholars (exclude scholars belonging to OTHER cohorts)
  const availableStudents = useMemo(() => {
    return students.filter((s: any) => {
      const { otherCohortName } = getStudentProps(s);
      return !otherCohortName;
    });
  }, [students, studentOtherCohortMap]);

  // Extract distinct grade values for the Select Dropdown
  const distinctGrades = useMemo(() => {
    const gradesSet = new Set<string>();
    availableStudents.forEach((s: any) => {
      const { grade } = getStudentProps(s);
      gradesSet.add(grade);
    });
    return Array.from(gradesSet).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [availableStudents]);

  // Filtered by Search & Profile Grade Dropdown
  const filteredStudents = useMemo(() => {
    return availableStudents.filter((s: any) => {
      const { fullName, email, grade } = getStudentProps(s);

      // Grade dropdown filter
      if (selectedGradeFilter !== 'ALL' && grade !== selectedGradeFilter) {
        return false;
      }

      // Search query filter
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        return fullName.toLowerCase().includes(q) || email.toLowerCase().includes(q);
      }

      return true;
    });
  }, [availableStudents, searchTerm, selectedGradeFilter]);

  const handleSelectAllFiltered = () => {
    const filteredIds = filteredStudents.map((s: any) => getStudentProps(s).studentId);
    const areAllSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedStudentIds.includes(id));

    if (areAllSelected) {
      setSelectedStudentIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedStudentIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const handleSave = async () => {
    if (!cohort) return;
    await onConfirm(cohort.id, selectedStudentIds, syncProfileGrade);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      {/* Header */}
      <DialogTitle sx={{ pb: 1.5, px: 2.5, pt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" fontWeight={700} fontSize="1.125rem" color="text.primary">
              Enroll Scholars
            </Typography>
            {cohort && (
              <Chip
                label={`${cohort.name}${cohort.className ? ` • ${cohort.className}` : ''}`}
                size="small"
                sx={{
                  bgcolor: 'primary.light',
                  color: 'primary.dark',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 22,
                }}
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            Select available scholars to enroll into this cohort section.
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <IoClose size={20} />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2.5 }}>
        {/* Search & Grade Filter Dropdown Toolbar */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {/* Search Input */}
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search scholar name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IoSearchOutline size={18} color="#94A3B8" />
                  </InputAdornment>
                ),
                sx: { borderRadius: 1.5, fontSize: '0.8125rem', bgcolor: 'background.paper', height: 38 },
              }}
            />
          </Grid>

          {/* Profile Grade Dropdown Filter */}
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth size="small">
              <Select
                value={selectedGradeFilter}
                onChange={(e) => setSelectedGradeFilter(e.target.value)}
                sx={{ borderRadius: 1.5, fontSize: '0.8125rem', bgcolor: 'background.paper', height: 38 }}
                displayEmpty
              >
                <MenuItem value="ALL" sx={{ fontSize: '0.8125rem' }}>
                  All Profile Grades ({availableStudents.length})
                </MenuItem>
                {distinctGrades.map((grade) => (
                  <MenuItem key={grade} value={grade} sx={{ fontSize: '0.8125rem' }}>
                    {grade === 'Unassigned' ? 'Unassigned Grade' : `Grade ${grade}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Selection Stats Bar */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, px: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {selectedStudentIds.length} scholar(s) selected
          </Typography>
          <Button size="small" onClick={handleSelectAllFiltered} sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}>
            {filteredStudents.length > 0 && filteredStudents.every((s: any) => selectedStudentIds.includes(getStudentProps(s).studentId))
              ? 'Deselect All'
              : 'Select All'}
          </Button>
        </Stack>

        {/* Scholars List */}
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
            <Typography variant="caption" sx={{ mt: 1.5, color: 'text.secondary', fontWeight: 500 }}>
              Loading scholars...
            </Typography>
          </Box>
        ) : filteredStudents.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {searchTerm || selectedGradeFilter !== 'ALL'
                ? 'No scholars match the selected filters.'
                : 'All scholars are already enrolled in cohorts.'}
            </Typography>
          </Box>
        ) : (
          <List
            disablePadding
            sx={{
              maxHeight: 320,
              overflow: 'auto',
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              p: 0.75,
            }}
          >
            {filteredStudents.map((s: any) => {
              const { studentId, fullName, email, grade, balance } = getStudentProps(s);
              const isChecked = selectedStudentIds.includes(studentId);
              const isCurrentlyInThisCohort = cohort?.students?.some((st) => st.studentId === studentId);

              return (
                <ListItem
                  key={studentId}
                  button
                  onClick={() => handleToggleStudent(studentId)}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.5,
                    p: 1,
                    bgcolor: isChecked ? 'rgba(37, 99, 235, 0.06)' : 'background.paper',
                    border: '1px solid',
                    borderColor: isChecked ? 'rgba(37, 99, 235, 0.35)' : 'divider',
                    transition: 'all 0.12s ease',
                    '&:hover': {
                      bgcolor: isChecked ? 'rgba(37, 99, 235, 0.1)' : 'grey.100',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={isChecked}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                      sx={{ color: 'divider', '&.Mui-checked': { color: 'primary.main' } }}
                    />
                  </ListItemIcon>

                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      bgcolor: isCurrentlyInThisCohort ? 'rgba(16, 185, 129, 0.15)' : 'primary.light',
                      color: isCurrentlyInThisCohort ? '#059669' : 'primary.dark',
                      mr: 1.25,
                    }}
                  >
                    {fullName.charAt(0)}
                  </Avatar>

                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2" fontWeight={600} color="text.primary" fontSize="0.8125rem">
                          {fullName}
                        </Typography>
                        {isCurrentlyInThisCohort && (
                          <Chip
                            label="Already Enrolled"
                            size="small"
                            sx={{
                              bgcolor: 'rgba(16, 185, 129, 0.12)',
                              color: '#059669',
                              fontWeight: 600,
                              fontSize: '0.625rem',
                              height: 18,
                            }}
                          />
                        )}
                      </Stack>
                    }
                    secondary={
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
                        <Typography variant="caption" color="text.secondary" fontSize="0.75rem">
                          {email}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          •
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={500} fontSize="0.75rem">
                          {grade === 'Unassigned' ? 'No Grade' : `Grade ${grade}`}
                        </Typography>
                      </Stack>
                    }
                  />

                  {/* Balance */}
                  <Chip
                    label={`$${balance.toLocaleString()}`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(16, 185, 129, 0.08)',
                      color: '#059669',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 22,
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        )}

        {/* Sync Profile Grade Permission Checkbox */}
        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={syncProfileGrade}
                onChange={(e) => setSyncProfileGrade(e.target.checked)}
                size="small"
                color="primary"
              />
            }
            label={
              <Typography variant="caption" color="text.primary" fontWeight={500} fontSize="0.8125rem">
                Sync enrolled scholars' profile grade to <strong>"{targetGradeName}"</strong>
              </Typography>
            }
          />
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
        <Button onClick={onClose} disabled={isSubmitting} sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSubmitting}
          sx={{
            fontWeight: 600,
            borderRadius: 1.5,
            textTransform: 'none',
            px: 2.5,
            boxShadow: 'none',
            '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
          }}
        >
          {isSubmitting ? 'Saving...' : `Save Enrollments (${selectedStudentIds.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
